import { useEffect, useState } from "react"
import clsx from "clsx"

import { golive, type Deployment } from "@/api/golive"
import type { GoLivePhase, GoLiveTask } from "@/types/golive"

/* --------------------------------------------------------------- modal shell */

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  footer?: React.ReactNode
  wide?: boolean
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose()
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-ink/40 p-4 pt-[8vh] backdrop-blur-sm"
      onMouseDown={onClose}
    >
      <div
        className={clsx(
          "w-full rounded-2xl bg-white shadow-card-hover ring-1 ring-slate-200",
          wide ? "max-w-2xl" : "max-w-md",
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-base font-bold text-ink">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-ink" aria-label="Close">
            ✕
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">{footer}</div>}
      </div>
    </div>
  )
}

const btn = "rounded-lg px-3.5 py-1.5 text-sm font-semibold transition"
const btnPrimary = clsx(btn, "bg-header text-white hover:bg-ink disabled:opacity-50")
const btnGhost = clsx(btn, "text-slate-500 hover:bg-slate-100")
const field =
  "w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-ink outline-none focus:border-headerSub focus:ring-2 focus:ring-headerSub/20"

function Err({ msg }: { msg: string | null }) {
  if (!msg) return null
  return <p className="mt-2 rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-700 ring-1 ring-red-200">{msg}</p>
}

/* ------------------------------------------------------------------ login */

export function LoginDialog({
  open,
  onClose,
  onLoggedIn,
}: {
  open: boolean
  onClose: () => void
  onLoggedIn: (name: string) => void
}) {
  const [code, setCode] = useState("")
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    setErr(null)
    try {
      const r = await golive.unlock(code.trim())
      onLoggedIn(r.name)
      setCode("")
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Enter access code"
      footer={
        <>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy || !code}>
            {busy ? "Unlocking…" : "Unlock editing"}
          </button>
        </>
      }
    >
      <p className="mb-3 text-xs text-slate-500">Viewing is open to everyone; editing needs your access code.</p>
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Access code</label>
      <input
        className={field}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        autoFocus
      />
      <Err msg={err} />
    </Modal>
  )
}

/* --------------------------------------------------------------- create */

function inferType(version: string): string {
  const n = version.trim().replace(/^v/i, "").split(".")
  const minor = parseInt(n[1] ?? "0", 10) || 0
  const patch = parseInt(n[2] ?? "0", 10) || 0
  if (patch > 0) return "hotfix"
  if (minor > 0) return "minor"
  return "major"
}

export function CreateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (d: Deployment) => void
}) {
  const [version, setVersion] = useState("")
  const [deployDate, setDate] = useState("")
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const type = version.trim() ? inferType(version) : ""

  const submit = async () => {
    setBusy(true)
    setErr(null)
    try {
      const d = await golive.create({ version: version.trim(), deployDate: deployDate || null })
      onCreated(d)
      setVersion("")
      setDate("")
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const seedNote =
    type === "hotfix"
      ? "Seeds from the Hotfix template (shorter run sheet)."
      : type
        ? "Seeds from your last full deployment (or the full template if none)."
        : ""

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New deployment"
      footer={
        <>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy || !version.trim()}>
            {busy ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        Version / Release
      </label>
      <input className={field} placeholder="e.g. v1.11.0" value={version} onChange={(e) => setVersion(e.target.value)} autoFocus />
      {type && (
        <p className="mt-1.5 text-xs text-slate-500">
          Detected <span className="font-bold uppercase text-header">{type}</span> release. {seedNote}
        </p>
      )}
      <label className="mb-1 mt-3 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        Deploy date <span className="font-normal normal-case text-slate-400">(optional — sets target dates)</span>
      </label>
      <input type="date" className={field} value={deployDate} onChange={(e) => setDate(e.target.value)} />
      <Err msg={err} />
    </Modal>
  )
}

/* ------------------------------------------------------- add / edit item */

const STATUS_OPTS = [
  ["not-started", "Not Started"],
  ["in-progress", "In Progress"],
  ["blocked", "Blocked"],
  ["done", "Done"],
  ["na", "N/A"],
] as const

export function ItemDialog({
  open,
  onClose,
  deployment,
  phase,
  task,
  people,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  deployment: Deployment
  phase: GoLivePhase
  task: GoLiveTask | null // null = add
  people: string[]
  onSaved: (d: Deployment) => void
}) {
  const [f, setF] = useState<Partial<GoLiveTask>>({})
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setF(
      task
        ? { ...task }
        : { title: "", owner: "", comments: "", status: "not-started", dependsOn: [], targetDate: "", targetTime: "" },
    )
    setErr(null)
  }, [task, open])

  const set = (patch: Partial<GoLiveTask>) => setF((p) => ({ ...p, ...patch }))

  // Owner as "A / B / freeform"; clicking a person chip toggles them in.
  const ownerTokens = (f.owner ?? "").split("/").map((s) => s.trim()).filter(Boolean)
  const toggleOwner = (name: string) => {
    const has = ownerTokens.some((t) => t.toLowerCase() === name.toLowerCase())
    const next = has
      ? ownerTokens.filter((t) => t.toLowerCase() !== name.toLowerCase())
      : [...ownerTokens, name]
    set({ owner: next.join(" / ") })
  }

  const submit = async () => {
    setBusy(true)
    setErr(null)
    try {
      let d: Deployment
      if (task) {
        d = await golive.patchItem(deployment.id, task.id, {
          title: f.title,
          owner: f.owner,
          roleLabel: f.roleLabel ?? "",
          comments: f.comments,
          status: f.status,
          targetDate: f.targetDate ?? "",
          targetTime: f.targetTime ?? "",
          dependsOn: f.dependsOn ?? [],
          gate: !!f.gate,
          critical: !!f.critical,
        })
      } else {
        d = await golive.addItem(deployment.id, {
          phaseId: phase.id,
          title: f.title,
          owner: f.owner,
          roleLabel: f.roleLabel ?? undefined,
          comments: f.comments,
          dependsOn: f.dependsOn ?? [],
          gate: f.gate || undefined,
          critical: f.critical || undefined,
        })
      }
      onSaved(d)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const depStr = (f.dependsOn ?? []).join(", ")

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={task ? `Edit task #${task.id}` : `Add task to ${phase.title}`}
      footer={
        <>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy || !f.title?.trim()}>
            {busy ? "Saving…" : task ? "Save changes" : "Add task"}
          </button>
        </>
      }
    >
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Task</label>
      <input className={field} value={f.title ?? ""} onChange={(e) => set({ title: e.target.value })} autoFocus />

      <div className="mt-3">
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Status</label>
        <select className={field} value={f.status} onChange={(e) => set({ status: e.target.value as GoLiveTask["status"] })}>
          {STATUS_OPTS.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>

      <label className="mb-1 mt-3 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        Assigned to <span className="font-normal normal-case text-slate-400">(pick people or type freeform)</span>
      </label>
      <input className={field} value={f.owner ?? ""} onChange={(e) => set({ owner: e.target.value })} placeholder="e.g. Monty / Sukhdeep or All Team" />
      <div className="mt-1.5 flex flex-wrap gap-1">
        {people.map((p) => {
          const on = ownerTokens.some((t) => t.toLowerCase() === p.toLowerCase())
          return (
            <button
              key={p}
              onClick={() => toggleOwner(p)}
              className={clsx(
                "rounded-full px-2 py-0.5 text-[11px] ring-1 transition",
                on ? "bg-headerSub/15 text-header ring-headerSub/30" : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50",
              )}
            >
              {p}
            </button>
          )
        })}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3">
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Target date</label>
          <input type="date" className={field} value={f.targetDate ?? ""} onChange={(e) => set({ targetDate: e.target.value })} />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Time</label>
          <input className={field} value={f.targetTime ?? ""} onChange={(e) => set({ targetTime: e.target.value })} placeholder="e.g. 01:00" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Depends on</label>
          <input
            className={field}
            value={depStr}
            onChange={(e) =>
              set({ dependsOn: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })
            }
            placeholder="e.g. 1, 2"
          />
        </div>
      </div>

      <label className="mb-1 mt-3 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Comments</label>
      <textarea className={clsx(field, "h-20 resize-y")} value={f.comments ?? ""} onChange={(e) => set({ comments: e.target.value })} />

      <div className="mt-3 flex gap-4 text-sm text-slate-600">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={!!f.gate} onChange={(e) => set({ gate: e.target.checked })} /> Gate
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={!!f.critical} onChange={(e) => set({ critical: e.target.checked })} /> Critical
        </label>
      </div>
      <Err msg={err} />
    </Modal>
  )
}

/* --------------------------------------------------------------- complete */

export function CompleteDialog({
  open,
  onClose,
  deployment,
  kind,
  onCompleted,
}: {
  open: boolean
  onClose: () => void
  deployment: Deployment
  kind: "deployment" | "rollback"
  onCompleted: (d: Deployment) => void
}) {
  const [notes, setNotes] = useState("")
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const rollback = kind === "rollback"

  // Deployment completion checks Phases 1-3; rollback completion checks the
  // Rollback phase.
  const incomplete = deployment.doc.phases
    .filter((p) => !!p.isRollback === rollback)
    .flatMap((p) => p.tasks)
    .filter((t) => t.status !== "done" && t.status !== "na")

  const submit = async () => {
    setBusy(true)
    setErr(null)
    try {
      const d = await golive.complete(deployment.id, notes, kind)
      onCompleted(d)
      setNotes("")
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const btnColor = rollback ? "bg-priority-p0 hover:bg-red-800" : "bg-win hover:bg-win-deep"

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={rollback ? "Complete rollback" : "Complete deployment"}
      footer={
        <>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={clsx(btn, "text-white disabled:opacity-50", btnColor)} onClick={submit} disabled={busy}>
            {busy ? "Completing…" : incomplete.length ? (rollback ? "Mark rolled back anyway" : "Complete anyway") : (rollback ? "Mark rolled back" : "Mark complete")}
          </button>
        </>
      }
    >
      {rollback && (
        <p className="mb-3 rounded-lg bg-red-50 p-2.5 text-xs text-red-700 ring-1 ring-red-200">
          This records the deployment as <strong>rolled back</strong> and locks it. Use this when the
          rollback plan (Phase 4) was executed instead of a successful go-live.
        </p>
      )}
      {incomplete.length > 0 ? (
        <div className="mb-3 rounded-lg bg-warn-light/70 p-3 ring-1 ring-warn/30">
          <p className="text-sm font-semibold text-warn">
            {incomplete.length} {rollback ? "rollback " : ""}item{incomplete.length > 1 ? "s" : ""} not finished
            {rollback ? "" : " (Rollback excluded)"}
          </p>
          <p className="mt-0.5 text-xs text-slate-600">
            You can still complete — these get recorded in the completion notes.
          </p>
          <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs text-slate-700">
            {incomplete.map((t) => (
              <li key={String(t.id)}>
                <span className="num text-slate-400">#{t.id}</span> {t.title}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mb-3 text-sm text-win-deep">
          {rollback ? "All rollback items are Done or N/A." : "All non-rollback items are Done or N/A. 🎉"}
        </p>
      )}
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Completion notes</label>
      <textarea className={clsx(field, "h-24 resize-y")} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Outcome, timing, anything notable…" />
      <p className="mt-2 text-xs text-slate-500">Completing locks the checklist to read-only; you can reopen it later.</p>
      <Err msg={err} />
    </Modal>
  )
}
